import { toast } from 'sonner';
import { GiftIcon } from 'lucide-react';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { useSite } from '../../../contexts/SiteContext';
import { useCatalog } from '../../../contexts/CatalogContext';
import { dayShort } from '../../../utils/account';
import { giftBoxIndex, subscriptionCadences, subscriptionRefill } from '../../../utils/refill';
import { euro } from '../../../core/format/currency';
import { IntervalChoice } from '../../shared/IntervalChoice';
import type { Subscription } from '../../../core/types/account';
type Props = {
  sub: Subscription;
  untilGift: number;
};

/**
 * Het pakketritme, met de komende dozen en wat erin zit. Alleen ritmes waarbij elke doos het
 * minimum haalt staan erbij (slim bijvullen, core/rules/refillPlan.ts); het huidige blijft altijd zichtbaar.
 */
export function SubscriptionRhythm({
  sub,
  untilGift
}: Props) {
  const {
    policy
  } = useSite();
  const {
    subscription
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const {
    boxes
  } = subscriptionRefill(sub, productById, policy.subscription);
  const choices = subscriptionCadences(sub, productById, policy.subscription);
  const giftAt = giftBoxIndex(boxes.slice(0, 3), untilGift);
  return <section aria-labelledby="rhythm-title" className="bg-shell p-5 ring-1 ring-line sm:p-6" data-id="element-6354" data-mp="26ffft31agqox">
      <h2 id="rhythm-title" className="text-[15px] text-ink" data-id="element-6355" data-mp="21hdmlpa5fazq">
        Je ritme
      </h2>
      <p id="rhythm-hint" className="mt-1 text-[13px] leading-relaxed text-muted" data-id="element-6356" data-mp="2z29nezd0bvkz">
        Elke {sub.intervalWeeks} weken een doos, met alleen wat bijna op is. Zo staat er nooit een tweede crème op de plank.
      </p>
      {choices.length > 1 && <div className="mt-4" data-id="element-6357" data-mp="2keom9azp06fp">
          <IntervalChoice intervals={choices} value={sub.intervalWeeks} labelledBy="rhythm-title" describedBy="rhythm-hint" onChange={async w => {
        if (await subscription({
          type: 'setInterval',
          weeks: w
        })) toast.success(`Ritme aangepast: elke ${w} weken een doos`);
      }} data-id="element-6358" data-mp="2kfgh2jlgu04x" />
          <p className="mt-2 text-[12px] text-muted" data-id="element-6359" data-mp="1g4wvcw3d5v66">Alleen ritmes waarbij elke doos minstens {euro(policy.subscription.minBoxValue)} is.</p>
        </div>}
      <h3 className="sr-only" data-id="element-6360" data-mp="11fh6xmrb21tn">Komende dozen</h3>
      <ol className="mt-5 divide-y divide-line border-t border-line" data-id="element-6361" data-mp="16etvcbxtrgyc">
        {boxes.slice(0, 3).map((b, i) => <li key={b.date} className="py-3" data-id="element-6362" data-mp="sf2kgk2lugok" data-mpi={i}>
            <p className="flex items-baseline justify-between gap-3 text-[14px]" data-id="element-6363" data-mp="shf0usspsizi">
              <span className="text-ink" data-id="element-6364" data-mp="wih0vbuggz2j">
                {dayShort(b.date)}
                {giftAt === i && <span className="ml-2 inline-flex items-center gap-1 text-[12px] text-clay" data-id="element-6365" data-mp="ij904ein9iw8">
                    <GiftIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-6366" data-mp="nghjujjfxuzl" /> met cadeau
                  </span>}
              </span>
              <span className="tabular-nums text-muted" data-id="element-6367" data-mp="2ht7ej3hmv881">{b.lines.length ? euro(b.total) : 'geen doos'}</span>
            </p>
            {b.lines.length > 0 && <p className="mt-0.5 text-[13px] text-muted" data-id="element-6368" data-mp="17u1hjb9tgvo">
                {b.lines.map(l => `${l.qty > 1 ? `${l.qty}× ` : ''}${productById(l.productId)?.name ?? l.productId}`).join(', ')}
              </p>}
          </li>)}
      </ol>
    </section>;
}